
import React, { useState } from 'react';
import { Lesson, UserProfile } from '../types';
import { getCurrentPlanStatus } from '../services/subscriptionStatus';

interface SettingsProps {
  onBackup: () => void;
  onRestore: (file: File) => void;
  onLogout: () => void;
  onSelectLesson: (lesson: Lesson) => void;
  favorites: Lesson[];
  profile: UserProfile;
  t: any;
}

const SettingsView: React.FC<SettingsProps> = ({ onBackup, onRestore, onLogout, onSelectLesson, favorites, profile, t }) => {
  const [currentTab, setCurrentTab] = useState<'general' | 'favorites'>('general');

  const getSubInfo = () => {
    const subscriptionStatus = getCurrentPlanStatus(profile);
    return {
      label: subscriptionStatus.plan === 'GOLD' ? t.planGold : t.planSilver,
      days: subscriptionStatus.remainingDays
    };
  };

  const subInfo = getSubInfo();

  return (
    <div className="space-y-8">
      <div className="flex bg-gray-100 p-1 rounded-xl">
        <button 
          onClick={() => setCurrentTab('general')}
          className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${currentTab === 'general' ? 'bg-white shadow text-yellow-700' : 'text-gray-500'}`}
        >
          {t.settings}
        </button>
        <button 
          onClick={() => setCurrentTab('favorites')}
          className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${currentTab === 'favorites' ? 'bg-white shadow text-yellow-700' : 'text-gray-500'}`}
        >
          {t.favorites}
        </button>
      </div>

      {currentTab === 'general' ? (
        <div className="space-y-4">
          {/* Subscription Info Widget */}
          <div className="p-5 bg-white border-2 border-yellow-50 rounded-2xl flex flex-col gap-3">
             <div className="flex justify-between items-center">
               <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">{t.subscription}</span>
               <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${profile.subscription.plan === 'GOLD' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-500'}`}>
                  {subInfo.label}
               </span>
             </div>
             <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${profile.subscription.plan === 'GOLD' ? 'bg-yellow-50 text-yellow-600' : 'bg-gray-50 text-gray-400'}`}>
                    <i className="fa-solid fa-clock"></i>
                  </div>
                  <span className="font-bold text-gray-800">{subInfo.days} {t.days} {t.remainingDays.split(' ')[1]}</span>
                </div>
                <i className="fa-solid fa-hourglass-half text-yellow-500/30"></i>
             </div>
          </div>

          <div className="p-4 bg-white border rounded-2xl flex items-center justify-between group active:bg-gray-50" onClick={onBackup}>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                <i className="fa-solid fa-download"></i>
              </div>
              <div>
                <p className="font-bold">{t.backup}</p>
                <p className="text-xs text-gray-400">Export local chiffré</p>
              </div>
            </div>
            <i className="fa-solid fa-chevron-right text-gray-300"></i>
          </div>

          <label className="p-4 bg-white border rounded-2xl flex items-center justify-between active:bg-gray-50 cursor-pointer">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center text-green-600">
                <i className="fa-solid fa-upload"></i>
              </div>
              <div>
                <p className="font-bold">{t.restore}</p>
                <p className="text-xs text-gray-400">Importer un fichier JSON</p>
              </div>
            </div>
            <input 
              type="file" 
              accept=".json" 
              className="hidden" 
              onChange={(e) => e.target.files && onRestore(e.target.files[0])} 
            />
            <i className="fa-solid fa-chevron-right text-gray-300"></i>
          </label>

          <div className="p-4 bg-white border rounded-2xl flex items-center justify-between text-red-600 active:bg-red-50" onClick={onLogout}>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <i className="fa-solid fa-right-from-bracket"></i>
              </div>
              <div>
                <p className="font-bold">Se déconnecter</p>
                <p className="text-xs opacity-70">Verrouiller l'accès local</p>
              </div>
            </div>
          </div>

          <div className="p-6 bg-gray-50 rounded-2xl border border-dashed border-gray-200 mt-4">
            <h4 className="font-bold text-gray-500 mb-2 uppercase text-xs tracking-widest flex items-center gap-2">
              <i className="fa-solid fa-shield-halved"></i>
              Sécurité Zero-Knowledge
            </h4>
            <p className="text-xs text-gray-400 leading-relaxed">
              Vos données sont chiffrées localement sur votre appareil. Personne (pas même nous) ne peut accéder à vos leçons ou notes sans votre code PIN de 6 chiffres.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {favorites.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              <i className="fa-regular fa-star text-4xl mb-4 block opacity-20"></i>
              <p>{t.noFavorites}</p>
            </div>
          ) : (
            favorites.map((l) => (
              <div 
                key={l.id} 
                onClick={() => onSelectLesson(l)}
                className="p-4 bg-white border rounded-2xl active:bg-yellow-50 transition-colors cursor-pointer flex items-center justify-between"
              >
                <div className="flex-1 overflow-hidden pr-4">
                  <p className="font-bold text-gray-900 truncate">{l.title}</p>
                  <p className="text-xs text-yellow-600 font-bold uppercase tracking-tighter">
                    {l.source} • {l.theme}
                  </p>
                </div>
                <i className="fa-solid fa-chevron-right text-gray-300 text-xs"></i>
              </div>
            ))
          )}
        </div>
      )}

      <div className="text-center text-gray-300 text-xs pt-4">
        Omen v1.0.0<br/>
        Propulsé par Gemini AI
      </div>
    </div>
  );
};

export default SettingsView;